import * as React from "react";
import type { ColumnDef } from "@tanstack/react-table";

import { RolePermission } from "api/rbacRoles";
import { Checkbox, DataTable, Tooltip } from "Components/ui";
import { PERMISSION_ACTIONS, RBAC_RESOURCES, RbacResourceDefinition } from "config/rbacCatalog";
import { actionsOf, isGranted, setGrants } from "./permissions";

interface RolePermissionFormProps {
  value: RolePermission[];
  onChange: (value: RolePermission[]) => void;
  disabled?: boolean;
}

/**
 * Areas × actions. A cell only exists where the server checks that pair, so every box means
 * something; hover or focus a box to read exactly what it allows.
 */
const RolePermissionForm: React.FC<RolePermissionFormProps> = ({ value, onChange, disabled }) => {
  const columns = React.useMemo<ColumnDef<RbacResourceDefinition>[]>(
    () => [
      {
        id: "area",
        header: "Area",
        cell: ({ row }) => {
          const resource = row.original;
          const actions = actionsOf(resource);
          const granted = actions.filter((action) => isGranted(value, resource.resource, action));
          const state: boolean | "indeterminate" =
            granted.length === 0
              ? false
              : granted.length === actions.length
              ? true
              : "indeterminate";
          return (
            <div className="flex items-start gap-3">
              <Checkbox
                className="mt-0.5"
                checked={state}
                disabled={disabled}
                aria-label={`All ${resource.label} permissions`}
                onCheckedChange={() =>
                  onChange(setGrants(value, resource.resource, actions, state !== true))
                }
              />
              <div className="min-w-0">
                <p className="font-medium">{resource.label}</p>
                <p className="text-xs text-muted-foreground">{resource.description}</p>
              </div>
            </div>
          );
        },
      },
      ...PERMISSION_ACTIONS.map<ColumnDef<RbacResourceDefinition>>(({ action, label }) => ({
        id: action,
        header: label,
        meta: { align: "center" },
        cell: ({ row }) => {
          const resource = row.original;
          const meaning = resource.actions[action];
          if (!meaning) {
            return (
              <span className="text-subtle-foreground" title="Not used for this area">
                —
              </span>
            );
          }
          return (
            <Tooltip content={meaning}>
              <span className="inline-flex">
                <Checkbox
                  checked={isGranted(value, resource.resource, action)}
                  disabled={disabled}
                  aria-label={`${resource.label}: ${label}. ${meaning}`}
                  onCheckedChange={(checked) =>
                    onChange(setGrants(value, resource.resource, [action], checked === true))
                  }
                />
              </span>
            </Tooltip>
          );
        },
      })),
    ],
    [disabled, onChange, value]
  );

  return (
    <DataTable
      columns={columns}
      data={RBAC_RESOURCES}
      getRowId={(row) => row.resource}
      enableSorting={false}
      density="compact"
      caption="Permissions by area"
    />
  );
};

export default RolePermissionForm;
